import { GridColDef } from '@mui/x-data-grid-pro';
import { compact, isEqual } from 'lodash';
import { useMemo } from 'react';

import { CommonBlock } from '@/components/blockEditor/blockEditor.types';
import { iconSizeByButtonSize } from '@/components/form/baseInputs/Button/styles';
import { Copy01Icon } from '@/components/icons/Copy01Icon';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { EyeOffIcon } from '@/components/icons/EyeOffIcon';
import { RefreshCcw01Icon } from '@/components/icons/RefreshCcw01Icon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { DeleteMenuItemWithConfirmationModal } from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import {
  ActionButtonRenderer,
  ActionType,
} from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import {
  EducationListItemType,
  getDefaultEducationTitle,
  getEducationSlides,
} from '@/modules/education/Education.constants';
import {
  CustomSlide,
  EducationSlideOverride,
  OwnedEducationSlideOverride,
  useEducationSlideOverrides,
  useOwnedEducationSlideOverrides,
} from '@/modules/education/EducationSlideOverrides/EducationSlideOverrides.context';
import { getEducationSlideDefaultData } from '@/modules/education/EducationSlideTrowser/EducationSlideDefaultData';
import {
  Operations,
  useCreateTenantSlideConfigMutation,
  useDeleteTenantSlideConfigMutation,
  useUpdateTenantSlideConfigMutation,
} from '@/modules/education/EducationSlideTrowser/graphql/EducationSlideTrowser.generated';
import {
  generateCustomSlideIdentifier,
  getSlideIdentifierFromEducationSlideId,
} from '@/modules/education/EducationSlideTrowser/utils/slideIdentifier.utils';
import { COLORS } from '@/styles/tokens/colors';
import { TenantSlideConfigConfigType } from '@/types/schema';

import { RowData } from './AdminEducationPagesTab';
import { EducationPagePublishingMenuItem } from './EducationPagePublishingMenuItem';

/**
 * Restructures CommonData blocks so they render correctly in the Draft component.
 * The Draft component uses <EditableRegion regionId="root" /> which expects
 * a single top-level block with id "root". Template pages may have multiple
 * top-level blocks (e.g., "description", "list-section") which won't render
 * in the Draft component. This function wraps them into a single "root" block.
 */
function wrapBlocksForDraftComponent(blocks: CommonBlock[]): CommonBlock[] {
  // If already a single 'root' block, no restructuring needed
  if (blocks.length === 1 && blocks[0]?.id === 'root') {
    return blocks;
  }

  // Flatten children from all top-level ListItemsSections into a single root
  const allChildren: CommonBlock[] = [];
  for (const block of blocks) {
    if (block.type === 'ListItemsSection' && block.children?.default) {
      allChildren.push(...block.children.default);
    } else {
      allChildren.push(block);
    }
  }

  return [
    {
      id: 'root',
      type: 'ListItemsSection',
      props: {},
      children: {
        default: allChildren,
      },
    },
  ];
}

const mutationOptions = {
  refetchQueries: [Operations.Query.GetEducationPageEditor],
  awaitRefetchQueries: true,
};

export function getEducationPagePublicationStatus(
  isParentTenant: boolean,
  ownedOverrides: OwnedEducationSlideOverride[]
) {
  if (!isParentTenant || ownedOverrides.length === 0) return null;

  const publishedCount = ownedOverrides.filter(
    (override) => override.isPublished
  ).length;
  if (publishedCount === ownedOverrides.length) {
    return {
      display: 'Published',
      state: 'published' as const,
      variant: BadgeVariants.Success,
    };
  }
  if (publishedCount > 0) {
    return {
      display: 'Partially published',
      state: 'partial' as const,
      variant: BadgeVariants.SecondaryOutline,
    };
  }
  return {
    display: 'Draft',
    state: 'draft' as const,
    variant: BadgeVariants.SecondaryOutline,
  };
}

export function useEducationPagesTableColumns(
  onNavigateToSlide?: (slideId: string) => void,
  isParentTenant = false
): GridColDef<RowData>[] {
  const { overridesMap, customSlidesMap } = useEducationSlideOverrides();
  const { ownedOverridesMap, error: ownedOverridesError } =
    useOwnedEducationSlideOverrides();
  const [createConfig] = useCreateTenantSlideConfigMutation(mutationOptions);
  const [updateConfig] = useUpdateTenantSlideConfigMutation(mutationOptions);
  const [deleteConfig] = useDeleteTenantSlideConfigMutation(mutationOptions);

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 300,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderCell: TwoLineTextRenderer<RowData, any>({
          lineOne: (params) => params.row.name,
          textAccompaniment: (params) => {
            // For custom pages, show "Hidden" badge or "created by X" tooltip
            if (params.row.isCustomPage) {
              const customSlide = customSlidesMap[params.row.id];
              if (customSlide?.isHidden) {
                return (
                  <Badge
                    variant={BadgeVariants.Warning}
                    display="Hidden"
                    wrapperSx={{ display: 'flex' }}
                  />
                );
              }
              if (params.row.createdByDisplayName) {
                return (
                  <InfoTooltip
                    title={`Created by ${params.row.createdByDisplayName}`}
                    placement="top"
                  />
                );
              }
              return null;
            }

            if (params.row.isOrganizationManaged) {
              return (
                <Badge
                  variant={BadgeVariants.SecondaryFilled}
                  display="Organization managed"
                  wrapperSx={{ display: 'flex' }}
                />
              );
            }

            const educationId = params.row.id as EducationListItemType;
            const slides = getEducationSlides(educationId);

            // Check if any page of this slide is hidden
            const isHidden = slides?.some((_, index) => {
              const slideIdentifier = getSlideIdentifierFromEducationSlideId(
                educationId,
                index
              );
              return !!overridesMap[slideIdentifier]?.isHidden;
            });

            if (isHidden) {
              return (
                <Badge
                  variant={BadgeVariants.Warning}
                  display="Hidden"
                  wrapperSx={{ display: 'flex' }}
                />
              );
            }

            // Check if any page of this slide is edited
            const isEdited = slides?.some((_, index) => {
              const slideIdentifier = getSlideIdentifierFromEducationSlideId(
                educationId,
                index
              );
              const override = overridesMap[slideIdentifier];
              if (!override) return false;

              // Check if displayName has been customized (different from default)
              const defaultTitle = getDefaultEducationTitle(educationId);
              if (
                override.displayName &&
                override.displayName !== defaultTitle
              ) {
                return true;
              }

              const defaultData = getEducationSlideDefaultData(
                educationId,
                index
              );

              // If no default data, this is a custom slide
              if (!defaultData) return false;

              // Compare contentJSON with defaultData
              return !isEqual(override.contentJSON, defaultData);
            });

            if (isEdited) {
              return (
                <Badge
                  variant={BadgeVariants.SecondaryFilled}
                  display="Edited"
                  wrapperSx={{ display: 'flex' }}
                />
              );
            }
            return null;
          },
        }),
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        minWidth: 200,
      },
      ...(isParentTenant
        ? [
            {
              field: 'sharing',
              headerName: 'Organization sharing',
              minWidth: 180,
              renderCell: ({ row }) => {
                if (row.isCustomPage) return <CellContainer />;

                const slides = getEducationSlides(
                  row.id as EducationListItemType
                );
                const ownedOverrides = compact(
                  slides?.map(
                    (_, index) =>
                      ownedOverridesMap[
                        getSlideIdentifierFromEducationSlideId(
                          row.id as EducationListItemType,
                          index
                        )
                      ]
                  )
                );
                const status = getEducationPagePublicationStatus(
                  isParentTenant,
                  ownedOverrides
                );

                return (
                  <CellContainer>
                    {status && (
                      <Badge
                        variant={status.variant}
                        display={status.display}
                      />
                    )}
                  </CellContainer>
                );
              },
            } satisfies GridColDef<RowData>,
          ]
        : []),
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        align: 'center',
        renderCell: ActionButtonRenderer({
          actions: ({ row }) =>
            getRowActions(row, {
              overridesMap,
              ownedOverridesMap,
              customSlidesMap,
              createConfig,
              updateConfig,
              deleteConfig,
              onNavigateToSlide,
              isParentTenant,
              ownedOverridesAvailable: !ownedOverridesError,
            }),
        }),
      },
    ],
    [
      overridesMap,
      ownedOverridesMap,
      customSlidesMap,
      createConfig,
      updateConfig,
      deleteConfig,
      onNavigateToSlide,
      isParentTenant,
      ownedOverridesError,
    ]
  );
}

interface RowActionsDependencies {
  overridesMap: Record<string, EducationSlideOverride>;
  ownedOverridesMap: Record<string, OwnedEducationSlideOverride>;
  customSlidesMap: Record<string, CustomSlide>;
  createConfig: ReturnType<typeof useCreateTenantSlideConfigMutation>[0];
  updateConfig: ReturnType<typeof useUpdateTenantSlideConfigMutation>[0];
  deleteConfig: ReturnType<typeof useDeleteTenantSlideConfigMutation>[0];
  onNavigateToSlide?: (slideId: string) => void;
  isParentTenant: boolean;
  ownedOverridesAvailable: boolean;
}

function getRowActions(
  row: RowData,
  {
    overridesMap,
    ownedOverridesMap,
    customSlidesMap,
    createConfig,
    updateConfig,
    deleteConfig,
    onNavigateToSlide,
    isParentTenant,
    ownedOverridesAvailable,
  }: RowActionsDependencies
): ActionType<RowData>[] {
  // Handle custom pages
  if (row.isCustomPage) {
    const customSlide = customSlidesMap[row.id];
    if (!customSlide) return [];

    const isHidden = customSlide.isHidden;

    const handleToggleHide = async () => {
      await updateConfig({
        variables: {
          input: {
            id: customSlide.id,
            update: {
              isHidden: !isHidden,
            },
          },
        },
      });
    };

    const handleDuplicate = async () => {
      const duplicatedName = `${customSlide.displayName || 'Untitled'} (Copy)`;
      // Deep clone contentJSON to ensure all nested blocks/children are fully copied
      const clonedContent = JSON.parse(JSON.stringify(customSlide.contentJSON));
      // Wrap blocks so the Draft component's <EditableRegion regionId="root" /> can render them
      const duplicatedContentJSON = {
        ...clonedContent,
        blocks: wrapBlocksForDraftComponent(clonedContent.blocks ?? []),
        educationCategory: row.category,
        duplicatedFrom: customSlide.slideIdentifier,
      };
      const newSlideIdentifier = generateCustomSlideIdentifier();
      await createConfig({
        variables: {
          input: {
            create: {
              configType: TenantSlideConfigConfigType.CustomSlideTemplate,
              slideIdentifier: newSlideIdentifier,
              contentJSON: duplicatedContentJSON,
              displayName: duplicatedName,
            },
          },
        },
      });
      // Open the newly created duplicate in the editor
      onNavigateToSlide?.(newSlideIdentifier);
    };

    const handleDelete = async () => {
      await deleteConfig({
        variables: {
          id: customSlide.id,
        },
      });
    };

    return [
      {
        label: () => 'Duplicate',
        icon: () => <Copy01Icon size={iconSizeByButtonSize.sm} />,
        handleClick: handleDuplicate,
      },
      {
        label: () => (isHidden ? 'Unhide' : 'Hide'),
        icon: () =>
          isHidden ? (
            <EyeIcon size={iconSizeByButtonSize.sm} />
          ) : (
            <EyeOffIcon size={iconSizeByButtonSize.sm} />
          ),
        handleClick: handleToggleHide,
      },
      {
        MenuItem: () => (
          <DeleteMenuItemWithConfirmationModal
            label="Delete"
            onConfirmDelete={handleDelete}
            modalProps={{
              heading: 'Delete custom page',
              children:
                'Are you sure you want to delete this custom page? This cannot be undone.',
            }}
          />
        ),
      },
    ];
  }

  // Handle template education pages
  const educationId = row.id as EducationListItemType;
  const slides = getEducationSlides(educationId);
  if (!slides) return [];
  if (!ownedOverridesAvailable) return [];

  // Check hidden state: hidden if at least one page is hidden
  const isHidden = slides.some((_, index) => {
    const identifier = getSlideIdentifierFromEducationSlideId(
      educationId,
      index
    );
    return ownedOverridesMap[identifier]?.isHidden;
  });

  // Check edited state: edited if at least one page has an override
  const isEdited = slides.some((_, index) => {
    const identifier = getSlideIdentifierFromEducationSlideId(
      educationId,
      index
    );
    const override = ownedOverridesMap[identifier];
    if (!override) return false;

    // Check if displayName has been customized (different from default)
    const defaultTitle = getDefaultEducationTitle(educationId);
    if (override.displayName && override.displayName !== defaultTitle) {
      return true;
    }

    const defaultData = getEducationSlideDefaultData(educationId, index);
    if (!defaultData) return true;

    return !isEqual(override.contentJSON, defaultData);
  });

  const handleToggleHide = async () => {
    const shouldHide = !isHidden;

    await Promise.all(
      slides.map(async (_, index) => {
        const identifier = getSlideIdentifierFromEducationSlideId(
          educationId,
          index
        );
        const existingOverride = ownedOverridesMap[identifier];

        if (existingOverride) {
          await updateConfig({
            variables: {
              input: {
                id: existingOverride.id,
                update: {
                  isHidden: shouldHide,
                },
              },
            },
          });
        } else if (shouldHide) {
          // Create hidden config if we are hiding and no config exists
          const defaultData = getEducationSlideDefaultData(educationId, index);
          if (defaultData) {
            await createConfig({
              variables: {
                input: {
                  create: {
                    configType:
                      TenantSlideConfigConfigType.EducationSlideOverride,
                    slideIdentifier: identifier,
                    contentJSON: defaultData,
                    isHidden: true,
                  },
                },
              },
            });
          }
        }
      })
    );
  };

  const handleRestore = async () => {
    await Promise.all(
      slides.map(async (_, index) => {
        const identifier = getSlideIdentifierFromEducationSlideId(
          educationId,
          index
        );
        const existingOverride = ownedOverridesMap[identifier];
        if (existingOverride) {
          await deleteConfig({
            variables: {
              id: existingOverride.id,
            },
          });
        }
      })
    );
  };

  const actions: ActionType<RowData>[] = [];
  const ownedOverrides = compact(
    slides.map(
      (_, index) =>
        ownedOverridesMap[
          getSlideIdentifierFromEducationSlideId(educationId, index)
        ]
    )
  );

  const publicationStatus = getEducationPagePublicationStatus(
    isParentTenant,
    ownedOverrides
  );
  if (publicationStatus) {
    const configIds = ownedOverrides.map((override) => override.id);
    if (publicationStatus.state !== 'published') {
      actions.push({
        MenuItem: () => (
          <EducationPagePublishingMenuItem
            action="publish"
            configIds={configIds}
          />
        ),
      });
    }
    if (publicationStatus.state !== 'draft') {
      actions.push({
        MenuItem: () => (
          <EducationPagePublishingMenuItem
            action="unpublish"
            configIds={configIds}
          />
        ),
      });
    }
  }

  // Duplicate action - creates a custom slide copy of the first page of the template.
  const handleDuplicate = async () => {
    const originalName = getDefaultEducationTitle(educationId) ?? row.name;
    const pageIdentifier = getSlideIdentifierFromEducationSlideId(
      educationId,
      0
    );
    const pageOverride = overridesMap[pageIdentifier];
    const contentJSON =
      pageOverride?.contentJSON ?? getEducationSlideDefaultData(educationId, 0);

    if (!contentJSON) return;

    const baseName = pageOverride?.displayName ?? originalName;
    const duplicatedName = `${baseName} (Copy)`;

    // Deep clone contentJSON to ensure all nested blocks/children are fully copied
    const clonedContent = JSON.parse(JSON.stringify(contentJSON));
    // Wrap blocks so the Draft component's <EditableRegion regionId="root" /> can render them
    const duplicatedContentJSON = {
      ...clonedContent,
      blocks: wrapBlocksForDraftComponent(clonedContent.blocks ?? []),
      educationCategory: row.category,
      duplicatedFrom: row.id,
    };

    const newSlideIdentifier = generateCustomSlideIdentifier();
    await createConfig({
      variables: {
        input: {
          create: {
            configType: TenantSlideConfigConfigType.CustomSlideTemplate,
            slideIdentifier: newSlideIdentifier,
            contentJSON: duplicatedContentJSON,
            displayName: duplicatedName,
          },
        },
      },
    });

    // Open the newly created duplicate in the editor
    onNavigateToSlide?.(newSlideIdentifier);
  };

  actions.push({
    label: () => 'Duplicate',
    icon: () => <Copy01Icon size={iconSizeByButtonSize.sm} />,
    handleClick: handleDuplicate,
  });

  if (row.isOrganizationManaged) return actions;

  // Hide/Unhide action
  actions.push({
    label: () => (isHidden ? 'Unhide' : 'Hide'),
    icon: () =>
      isHidden ? (
        <EyeIcon size={iconSizeByButtonSize.sm} />
      ) : (
        <EyeOffIcon size={iconSizeByButtonSize.sm} />
      ),
    handleClick: handleToggleHide,
  });

  // Restore action - show if edited or hidden
  if (isEdited || isHidden) {
    actions.push({
      MenuItem: () => (
        <DeleteMenuItemWithConfirmationModal
          label="Restore to original"
          onConfirmDelete={handleRestore}
          menuItemProps={{
            icon: (
              <RefreshCcw01Icon
                color={COLORS.FUNCTIONAL.ERROR.DEFAULT}
                size={18}
              />
            ),
          }}
          modalProps={{
            heading: 'Restore to original',
            confirmText: 'Restore',
            children:
              'Are you sure you want to restore this page to its original content? This will discard any custom edits.',
          }}
        />
      ),
    });
  }

  return actions;
}
